import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import ManualServiceHoursModal from "./ManualServiceHoursModal";
import { useLocationContext } from "../../../contexts/LocationContext";
import { apiClient } from "../../../services/api/client";

const ServiceHourTracking = ({ employee }) => {
  const { locations } = useLocationContext();
  const [showManualModal, setShowManualModal] = useState(false);
  const [clockRecords, setClockRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date filters - default to last 7 days
  const [dateFilters, setDateFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  // Fetch clock records
  useEffect(() => {
    if (employee?.id) {
      fetchClockRecords();
    }
  }, [employee?.id, dateFilters]);

  const fetchClockRecords = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.get("/clockrecords", {
        employeeId: employee.id,
        fromDate: dateFilters.startDate,
        toDate: dateFilters.endDate,
      });

      if (data.success) {
        setClockRecords(data.data || []);
      } else {
        setClockRecords([]);
      }
    } catch (err) {
      console.error("Error fetching clock records:", err);
      setError(err.message);
      setClockRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntrySubmit = async (payload) => {
    try {
      const data = await apiClient.post("/clockrecords", payload);

      if (data.success) {
        // Refresh clock records
        await fetchClockRecords();
        return true;
      } else {
        throw new Error(data.message || "Failed to add service hours");
      }
    } catch (err) {
      throw err;
    }
  };

  const handleDateFilterChange = (field, value) => {
    setDateFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuickFilter = (days) => {
    const endDate = new Date().toISOString().split("T")[0];
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    setDateFilters({ startDate, endDate });
  };

  // Calculate stats
  const totalHours = clockRecords.reduce(
    (sum, record) => sum + (record.hoursWorked || 0),
    0,
  );
  const eligibilityThreshold = 20; // Hours per week for eligibility
  const weekCount =
    Math.ceil(
      (new Date(dateFilters.endDate) - new Date(dateFilters.startDate)) /
        (7 * 24 * 60 * 60 * 1000),
    ) || 1;
  const averagePerWeek = (totalHours / weekCount).toFixed(1);

  // Get location name helper
  const getLocationName = (locationId) => {
    const location = locations?.find((l) => l.id === locationId);
    return location?.name || "Unknown Location";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Service Hour Tracking
        </h3>
        <Button
          variant="primary"
          size="sm"
          iconName="Plus"
          onClick={() => setShowManualModal(true)}
        >
          Add Manual Entry
        </Button>
      </div>

      {/* Date Filters */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={18} className="text-muted-foreground" />
            <span className="text-sm font-medium">Filter by Date:</span>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFilters.startDate}
              onChange={(e) =>
                handleDateFilterChange("startDate", e.target.value)
              }
              max={dateFilters.endDate}
              className="text-sm"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateFilters.endDate}
              onChange={(e) =>
                handleDateFilterChange("endDate", e.target.value)
              }
              min={dateFilters.startDate}
              max={new Date().toISOString().split("T")[0]}
              className="text-sm"
            />
          </div>

          {/* Quick Filters */}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => handleQuickFilter(7)}
              className="btn btn-ghost btn-sm"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => handleQuickFilter(30)}
              className="btn btn-ghost btn-sm"
            >
              Last 30 Days
            </button>
            <button
              onClick={() => handleQuickFilter(90)}
              className="btn btn-ghost btn-sm"
            >
              Last 90 Days
            </button>
          </div>
        </div>
      </div>

      {/* Monthly Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Clock" size={16} color="var(--color-primary)" />
            <p className="text-xs font-medium text-gray-600">Total Hours</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {totalHours.toFixed(1)}h
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} color="var(--color-success)" />
            <p className="text-xs font-medium text-gray-600">Weekly Avg</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{averagePerWeek}h</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Target" size={16} color="var(--color-secondary)" />
            <p className="text-xs font-medium text-gray-600">Threshold</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {eligibilityThreshold}h
          </p>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle" size={16} color="var(--color-accent)" />
            <p className="text-xs font-medium text-gray-600">Status</p>
          </div>
          <p className="text-sm font-bold text-gray-900">
            {parseFloat(averagePerWeek) >= eligibilityThreshold
              ? "Above Threshold"
              : "Below Threshold"}
          </p>
        </div>
      </div>

      {/* Eligibility Progress */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-gray-900">
            Weekly Eligibility Progress
          </h4>
          <span
            className="text-sm font-medium"
            style={{ color: "var(--color-success)" }}
          >
            {averagePerWeek}h / {eligibilityThreshold}h
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="h-3 rounded-full transition-all duration-300"
            style={{
              width: `${Math.min((parseFloat(averagePerWeek) / eligibilityThreshold) * 100, 100)}%`,
              backgroundColor:
                parseFloat(averagePerWeek) >= eligibilityThreshold
                  ? "var(--color-success)"
                  : "var(--color-warning)",
            }}
          />
        </div>
        <p className="text-xs text-gray-600 mt-2">
          {parseFloat(averagePerWeek) >= eligibilityThreshold
            ? "✓ Meets minimum hours requirement for TRONC eligibility"
            : `${(eligibilityThreshold - parseFloat(averagePerWeek)).toFixed(1)}h more needed per week to meet threshold`}
        </p>
      </div>

      {/* Clock Records Table */}
      <div>
        <h4 className="text-md font-semibold text-gray-900 mb-4">
          Service Hours ({clockRecords.length} records)
        </h4>

        {loading ? (
          <div className="card p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">Loading service hours...</p>
          </div>
        ) : error ? (
          <div className="alert alert-warning">
            <Icon name="AlertTriangle" size={20} />
            <span>{error}</span>
          </div>
        ) : clockRecords.length === 0 ? (
          <div className="card p-8 text-center">
            <Icon
              name="Clock"
              size={32}
              className="mx-auto mb-4 text-muted-foreground"
            />
            <p className="text-muted-foreground mb-4">
              No service hours recorded for this period
            </p>
            <Button
              variant="primary"
              size="sm"
              iconName="Plus"
              onClick={() => setShowManualModal(true)}
            >
              Add Manual Entry
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Clock In
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Clock Out
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Hours
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clockRecords.map((record) => {
                    const clockInDate = new Date(record.clockIn);
                    const clockOutDate = record.clockOut
                      ? new Date(record.clockOut)
                      : null;

                    return (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {clockInDate.toLocaleDateString("en-GB")}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <Icon
                              name="MapPin"
                              size={14}
                              className="text-muted-foreground"
                            />
                            {record.location || "N/A"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {clockInDate.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {clockOutDate
                            ? clockOutDate.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "In Progress"}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {record.hoursWorked
                            ? record.hoursWorked.toFixed(2)
                            : "-"}
                          h
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              record.isLocallyModified
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            <Icon
                              name={record.isLocallyModified ? "Edit" : "Cpu"}
                              size={10}
                            />
                            {record.isLocallyModified
                              ? "Manual"
                              : "Till System"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  <tr>
                    <td
                      colSpan="4"
                      className="px-4 py-3 text-sm font-semibold text-gray-900"
                    >
                      Total
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">
                      {totalHours.toFixed(2)}h
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Integration Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon
            name="Info"
            size={20}
            color="var(--color-primary)"
            className="mt-0.5"
          />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">
              Automated Till Integration
            </h4>
            <p className="text-sm text-gray-700">
              Service hours are automatically tracked from your till system
              clock-in/out records. Manual entries can be added for cash-only
              shifts or special circumstances.
            </p>
          </div>
        </div>
      </div>

      {/* Manual Entry Modal */}
      <ManualServiceHoursModal
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        onSubmit={handleManualEntrySubmit}
        employee={employee}
        locations={locations}
      />
    </div>
  );
};

export default ServiceHourTracking;
